import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Send, User, Search, Plus, X, Trash2, 
  Check, CheckCheck, MessageCircle, Users, ArrowLeft
} from 'lucide-react';
import { messagesAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from './AdminLayout';

const AdminMessaging = () => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [showNewChat, setShowNewChat] = useState(false);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const { toast } = useToast();

  useEffect(() => {
    loadConversations();
    loadContacts();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadConversations = async () => {
    setLoading(true);
    try {
      const res = await messagesAPI.getAll();
      // Group messages by conversation partner
      const grouped = groupMessagesByConversation(res.data);
      setConversations(grouped);
    } catch (error) {
      console.error('Error loading conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const groupMessagesByConversation = (allMessages) => {
    const convMap = new Map();
    
    allMessages.forEach(msg => {
      // Determine the other party in the conversation
      const isFromMe = msg.sender_id === user?.id;
      const partnerId = isFromMe ? msg.recipient_id : msg.sender_id;
      const partnerName = isFromMe ? msg.recipient_name : msg.sender_name;
      
      if (!convMap.has(partnerId)) {
        convMap.set(partnerId, {
          partnerId,
          partnerName,
          messages: [],
          lastMessage: msg,
          unreadCount: 0
        });
      }
      
      const conv = convMap.get(partnerId);
      conv.messages.push(msg);
      
      if (!isFromMe && !msg.is_read) {
        conv.unreadCount++;
      }
      
      // Update last message if this one is more recent
      if (new Date(msg.created_at) > new Date(conv.lastMessage.created_at)) {
        conv.lastMessage = msg;
      }
    });
    
    // Sort conversations by last message date
    return Array.from(convMap.values()).sort((a, b) => 
      new Date(b.lastMessage.created_at) - new Date(a.lastMessage.created_at)
    );
  };

  const loadContacts = async () => {
    try {
      const res = await messagesAPI.getContacts();
      setContacts(res.data);
    } catch (error) {
      console.error('Error loading contacts:', error);
    }
  };

  const selectConversation = async (conv) => {
    setSelectedConversation(conv);
    setMessages(conv.messages.sort((a, b) => 
      new Date(a.created_at) - new Date(b.created_at)
    ));
    
    // Mark unread messages as read
    for (const msg of conv.messages) {
      if (!msg.is_read && msg.recipient_id === user?.id) {
        try {
          await messagesAPI.markRead(msg.id);
        } catch (error) {
          console.error('Error marking read:', error);
        }
      }
    }
    
    // Update local state
    setConversations(prev => prev.map(c => 
      c.partnerId === conv.partnerId ? { ...c, unreadCount: 0 } : c
    ));
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;
    
    setSending(true);
    try {
      const res = await messagesAPI.send({
        recipient_id: selectedConversation.partnerId,
        subject: 'Chat',
        content: newMessage.trim()
      });
      
      // Add message to local state
      const sentMessage = {
        id: res.data.id,
        sender_id: user?.id,
        sender_name: `${user?.prenom} ${user?.nom}`,
        recipient_id: selectedConversation.partnerId,
        recipient_name: selectedConversation.partnerName,
        content: newMessage.trim(),
        created_at: new Date().toISOString(),
        is_read: false
      };
      
      setMessages(prev => [...prev, sentMessage]);
      setNewMessage('');
      
      // Update conversation
      setConversations(prev => prev.map(c => 
        c.partnerId === selectedConversation.partnerId 
          ? { ...c, lastMessage: sentMessage, messages: [...c.messages, sentMessage] }
          : c
      ));
    } catch (error) {
      toast({ title: 'Erreur', description: 'Impossible d\'envoyer le message', variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  const startNewConversation = async (contact) => {
    // Check if conversation already exists
    const existing = conversations.find(c => c.partnerId === contact.id);
    if (existing) {
      selectConversation(existing);
    } else {
      setSelectedConversation({
        partnerId: contact.id,
        partnerName: `${contact.prenom} ${contact.nom}`,
        messages: [],
        unreadCount: 0
      });
      setMessages([]);
    }
    setShowNewChat(false);
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days === 0) {
      return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    } else if (days === 1) {
      return 'Hier';
    } else if (days < 7) {
      return date.toLocaleDateString('fr-FR', { weekday: 'short' });
    }
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  const filteredContacts = contacts.filter(c =>
    `${c.prenom} ${c.nom}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalUnread = conversations.reduce((sum, c) => sum + c.unreadCount, 0);

  return (
    <AdminLayout>
      <div className="h-[calc(100vh-120px)] flex flex-col" data-testid="admin-messaging">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-[#2C3C65] flex items-center gap-2">
              <MessageCircle className="h-6 w-6" />
              Messagerie
              {totalUnread > 0 && (
                <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {totalUnread}
                </span>
              )}
            </h1>
            <p className="text-sm text-[#6777A1]">Échangez avec les élèves et parents</p>
          </div>
          <Button
            onClick={() => setShowNewChat(true)}
            className="bg-[#285BBB] hover:bg-[#2C3C65]"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nouvelle conversation
          </Button>
        </div>

        {/* Chat Container */}
        <div className="flex-1 flex bg-white rounded-2xl shadow-lg overflow-hidden border">
          {/* Conversations List */}
          <div className={`w-full md:w-80 lg:w-96 border-r flex flex-col ${selectedConversation ? 'hidden md:flex' : 'flex'}`}>
            {/* Search */}
            <div className="p-4 border-b">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Rechercher une conversation..."
                  className="pl-10 bg-gray-50 border-0"
                />
              </div>
            </div>

            {/* Conversations */}
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="p-8 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#285BBB] mx-auto"></div>
                </div>
              ) : conversations.length === 0 ? (
                <div className="p-8 text-center">
                  <MessageCircle className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-[#6777A1]">Aucune conversation</p>
                  <p className="text-sm text-gray-400 mt-1">Commencez une nouvelle discussion</p>
                </div>
              ) : (
                conversations.map((conv) => (
                  <div
                    key={conv.partnerId}
                    onClick={() => selectConversation(conv)}
                    className={`p-4 cursor-pointer border-b hover:bg-gray-50 transition-colors ${
                      selectedConversation?.partnerId === conv.partnerId ? 'bg-blue-50 border-l-4 border-l-[#285BBB]' : ''
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold ${
                        conv.unreadCount > 0 ? 'bg-[#285BBB]' : 'bg-gray-400'
                      }`}>
                        {conv.partnerName?.split(' ').map(n => n[0]).join('').substring(0, 2)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <span className={`font-medium truncate ${conv.unreadCount > 0 ? 'text-[#2C3C65]' : 'text-gray-600'}`}>
                            {conv.partnerName}
                          </span>
                          <span className="text-xs text-gray-400 flex-shrink-0 ml-2">
                            {formatTime(conv.lastMessage.created_at)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between mt-1">
                          <p className={`text-sm truncate ${conv.unreadCount > 0 ? 'text-[#2C3C65] font-medium' : 'text-gray-500'}`}>
                            {conv.lastMessage.sender_id === user?.id && (
                              <span className="text-gray-400 mr-1">Vous:</span>
                            )}
                            {conv.lastMessage.content.substring(0, 40)}...
                          </p>
                          {conv.unreadCount > 0 && (
                            <span className="bg-[#285BBB] text-white text-xs w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ml-2">
                              {conv.unreadCount}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Chat View */}
          <div className={`flex-1 flex flex-col ${!selectedConversation ? 'hidden md:flex' : 'flex'}`}>
            {selectedConversation ? (
              <>
                {/* Chat Header */}
                <div className="p-4 border-b bg-gray-50 flex items-center gap-3">
                  <button
                    onClick={() => setSelectedConversation(null)}
                    className="md:hidden p-2 hover:bg-gray-200 rounded-lg"
                  >
                    <ArrowLeft className="h-5 w-5 text-[#2C3C65]" />
                  </button>
                  <div className="w-10 h-10 rounded-full bg-[#285BBB] flex items-center justify-center text-white font-semibold">
                    {selectedConversation.partnerName?.split(' ').map(n => n[0]).join('').substring(0, 2)}
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#2C3C65]">{selectedConversation.partnerName}</h3>
                    <p className="text-xs text-gray-500">En ligne</p>
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-[#f8fafc]">
                  {messages.length === 0 ? (
                    <div className="text-center py-12">
                      <MessageCircle className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-500">Aucun message</p>
                      <p className="text-sm text-gray-400">Envoyez votre premier message</p>
                    </div>
                  ) : (
                    messages.map((msg, index) => {
                      const isFromMe = msg.sender_id === user?.id;
                      const showDate = index === 0 || 
                        new Date(msg.created_at).toDateString() !== new Date(messages[index - 1].created_at).toDateString();
                      
                      return (
                        <React.Fragment key={msg.id}>
                          {showDate && (
                            <div className="text-center my-4">
                              <span className="text-xs text-gray-400 bg-white px-3 py-1 rounded-full shadow-sm">
                                {new Date(msg.created_at).toLocaleDateString('fr-FR', { 
                                  weekday: 'long', 
                                  day: 'numeric', 
                                  month: 'long' 
                                })}
                              </span>
                            </div>
                          )}
                          <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className={`flex ${isFromMe ? 'justify-end' : 'justify-start'}`}
                          >
                            <div className={`max-w-[75%] ${isFromMe ? 'order-2' : 'order-1'}`}>
                              <div
                                className={`px-4 py-3 rounded-2xl ${
                                  isFromMe
                                    ? 'bg-[#285BBB] text-white rounded-br-md'
                                    : 'bg-white text-[#2C3C65] rounded-bl-md shadow-sm'
                                }`}
                              >
                                <p className="whitespace-pre-wrap break-words">{msg.content}</p>
                              </div>
                              <div className={`flex items-center gap-1 mt-1 ${isFromMe ? 'justify-end' : 'justify-start'}`}>
                                <span className="text-xs text-gray-400">
                                  {new Date(msg.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                </span>
                                {isFromMe && (
                                  msg.is_read ? (
                                    <CheckCheck className="h-3 w-3 text-blue-500" />
                                  ) : (
                                    <Check className="h-3 w-3 text-gray-400" />
                                  )
                                )}
                              </div>
                            </div>
                          </motion.div>
                        </React.Fragment>
                      );
                    })
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Message Input */}
                <div className="p-4 border-t bg-white">
                  <div className="flex items-end gap-3">
                    <div className="flex-1">
                      <textarea
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSendMessage();
                          }
                        }}
                        placeholder="Écrivez votre message..."
                        className="w-full px-4 py-3 bg-gray-100 rounded-2xl resize-none focus:outline-none focus:ring-2 focus:ring-[#285BBB] max-h-32"
                        rows={1}
                      />
                    </div>
                    <Button
                      onClick={handleSendMessage}
                      disabled={sending || !newMessage.trim()}
                      className="bg-[#285BBB] hover:bg-[#2C3C65] rounded-full w-12 h-12 p-0"
                    >
                      {sending ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                      ) : (
                        <Send className="h-5 w-5" />
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-gray-400 mt-2 text-center">
                    Appuyez sur Entrée pour envoyer, Shift+Entrée pour un retour à la ligne
                  </p>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center bg-[#f8fafc]">
                <div className="text-center">
                  <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <MessageCircle className="h-12 w-12 text-gray-300" />
                  </div>
                  <h3 className="text-xl font-semibold text-[#2C3C65] mb-2">Messagerie École</h3>
                  <p className="text-[#6777A1] mb-4">Sélectionnez une conversation ou démarrez-en une nouvelle</p>
                  <Button
                    onClick={() => setShowNewChat(true)}
                    variant="outline"
                    className="border-[#285BBB] text-[#285BBB]"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Nouvelle conversation
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* New Chat Modal */}
        <AnimatePresence>
          {showNewChat && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
              onClick={() => setShowNewChat(false)}
            >
              <motion.div
                initial={{ scale: 0.95, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.95, y: 20 }}
                className="bg-white rounded-2xl shadow-2xl w-full max-w-md"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-4 border-b flex items-center justify-between">
                  <h3 className="font-bold text-lg text-[#2C3C65]">Nouvelle conversation</h3>
                  <button onClick={() => setShowNewChat(false)} className="p-1 hover:bg-gray-100 rounded-full">
                    <X className="h-5 w-5 text-gray-500" />
                  </button>
                </div>
                
                <div className="p-4">
                  <div className="relative mb-4">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Rechercher un contact..."
                      className="pl-10"
                    />
                  </div>
                  
                  <div className="max-h-80 overflow-y-auto">
                    {filteredContacts.length === 0 ? (
                      <p className="text-center text-gray-500 py-4">Aucun contact trouvé</p>
                    ) : (
                      filteredContacts.map((contact) => (
                        <div
                          key={contact.id}
                          onClick={() => startNewConversation(contact)}
                          className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-xl cursor-pointer transition-colors"
                        >
                          <div className="w-10 h-10 rounded-full bg-[#285BBB] flex items-center justify-center text-white font-medium">
                            {contact.prenom?.[0]}{contact.nom?.[0]}
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-[#2C3C65]">{contact.prenom} {contact.nom}</p>
                            <p className="text-sm text-gray-500">{contact.email}</p>
                          </div>
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            contact.role === 'eleve' ? 'bg-green-100 text-green-700' :
                            contact.role === 'parent' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-blue-100 text-blue-700'
                          }`}>
                            {contact.role === 'eleve' ? 'Élève' : 
                             contact.role === 'parent' ? 'Parent' : 
                             contact.role === 'agence' ? 'Agence' : 'Admin'}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </AdminLayout>
  );
};

export default AdminMessaging;
